import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { MANUALS, type ManualIcon, type ManualLang } from './manualsRegistry'

interface Props {
  dark: boolean
  onClose: () => void
}

// ── Iconos ────────────────────────────────────────────────────────────────────

function ModuleIcon({ icon, className }: { icon: ManualIcon; className?: string }) {
  switch (icon) {
    case 'sequence':
      return (
        <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 4h12M2 8h12M2 12h8" /><circle cx="13.5" cy="12" r="1.5" />
        </svg>
      )
    case 'week':
      return (
        <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <rect x="2" y="2" width="12" height="12" rx="1.5" />
          <path d="M2 5.5h12M5 2v3.5M11 2v3.5" />
          <path d="M4.5 8.5h2.5M9 8.5h2.5M4.5 11h2.5M9 11h2.5" />
        </svg>
      )
    case 'report':
      // Barras sobre una linea base. La tabla, que seria lo literal, se confundia
      // a 16 px con el calendario del Gantt: los dos son un marco con divisiones.
      return (
        <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 13.5h12" />
          <path d="M4.25 11.5V8M8 11.5V4M11.75 11.5V6.5" />
        </svg>
      )
    case 'datasheet':
      return (
        <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 1.5h5L13 5v9.5a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5V2a.5.5 0 01.5-.5z" />
          <path d="M9 1.5V5h4M5.5 8.5h5M5.5 11h3" />
        </svg>
      )
    default:
      return null
  }
}

const IconDownload = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v7M5 7l3 3 3-3M3 13h10" />
  </svg>
)

// ── Componente principal ──────────────────────────────────────────────────────

export function HelpCenterPanel({ dark, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [open, setOpen] = useState(false)

  // Manual en el idioma de la interfaz; si no existe versión, cae al español
  const lang: ManualLang = i18n.resolvedLanguage === 'en' ? 'en' : 'es'

  // Animar entrada
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 340)
  }

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [])

  // ── Tema ──────────────────────────────────────────────────────────────────
  const bg      = dark ? 'bg-[#0d1117]'       : 'bg-white'
  const cardBg  = dark ? 'bg-[#161b22]'       : 'bg-slate-50'
  const border  = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const mainTxt = dark ? 'text-slate-100'      : 'text-slate-900'
  const mutedTxt = dark ? 'text-slate-400'     : 'text-slate-500'
  const dimTxt  = dark ? 'text-slate-600'      : 'text-slate-400'

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">

      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        onClick={handleClose}
        className="absolute inset-0 cursor-pointer"
        style={{
          backgroundColor: open ? 'rgba(0,0,0,0.52)' : 'rgba(0,0,0,0)',
          backdropFilter: open ? 'blur(3px)' : 'blur(0px)',
          transition: 'background-color 0.34s ease, backdrop-filter 0.34s ease',
        }}
      />

      {/* ── Panel lateral ─────────────────────────────────────────────────── */}
      <div
        className={`relative flex flex-col h-full shadow-2xl ${bg} border-l ${border}`}
        style={{
          width: isMobile ? '100vw' : 'min(94vw, 440px)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.34s cubic-bezier(0.32, 0, 0.1, 1)',
          boxShadow: dark
            ? '-32px 0 80px rgba(0,0,0,0.6), -1px 0 0 rgba(255,255,255,0.05)'
            : '-32px 0 80px rgba(0,0,0,0.12), -1px 0 0 rgba(0,0,0,0.06)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('help.title')}
      >

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className={`flex items-center gap-4 px-6 py-4 border-b ${border} shrink-0`}>
          <div className={`p-2 rounded-lg ${dark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <svg className={`w-4 h-4 ${mutedTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.2 9.2a2.8 2.8 0 115.1 1.6c-.7 1-1.8 1.3-2.3 2.2-.2.3-.3.7-.3 1.2" />
              <circle cx="12" cy="17.2" r="0.4" fill="currentColor" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[13px] font-semibold ${mainTxt}`}>{t('help.title')}</h2>
            <p className={`text-[11px] ${mutedTxt} mt-0.5`}>{t('help.subtitle')}</p>
          </div>
          <button
            onClick={handleClose}
            aria-label={t('help.close')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors cursor-pointer ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span className={`text-[10px] font-mono hidden sm:inline ${dimTxt}`}>ESC</span>
          </button>
        </div>

        {/* ── Cuerpo: lista de manuales ───────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          <div className={`text-[9px] font-bold uppercase tracking-widest mb-3 select-none ${dimTxt}`}>
            {t('help.manualsSection')}
          </div>

          <div className="flex flex-col gap-3">
            {MANUALS.map(m => {
              const f = m.files[lang] ?? m.files.es
              return (
                <div key={m.id} className={`rounded-xl border ${border} ${cardBg} p-4`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg shrink-0 ${dark ? 'bg-sky-500/10 text-sky-300' : 'bg-sky-50 text-sky-600'}`}>
                      <ModuleIcon icon={m.icon} className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[13px] font-semibold ${mainTxt}`}>{t(m.titleKey)}</div>
                      <p className={`text-[11px] leading-relaxed mt-0.5 ${mutedTxt}`}>{t(m.descKey)}</p>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between mt-3 pt-3 border-t ${border}`}>
                    <span className={`text-[10px] font-mono uppercase tracking-wide ${dimTxt}`}>
                      PDF · {f.sizeLabel}
                    </span>
                    <a
                      href={f.file}
                      download={f.fileName}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-sky-600 text-white hover:bg-sky-500 active:bg-sky-700 transition-colors"
                    >
                      <IconDownload />
                      {t('help.download')}
                    </a>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className={`px-6 py-3.5 border-t ${border} shrink-0`}>
          <p className={`text-[11px] ${dimTxt}`}>{t('help.contact')}</p>
        </div>
      </div>
    </div>,
    document.body,
  )
}
